// pages/admin/AdminTeardownsPage.tsx — the teardown queue.
// (TF3-1, owner + planning 10-14 05:00) The clone board (+ New clone, the attach picker, the storyboard), Template and
// Remix are DELETED: they made paid generation outside the engine, and a cover spoke the original's words. A teardown is
// research: C1.7's `recreate` reads its structure.
import { useState, useEffect, useCallback } from "react";
import { EngineSignInBanner } from "./AdminEngineConsolePage";
import { teardownsApi, type TeardownJob } from "../../api/covers-teardowns.ts";
import { translateError } from "../../lib/covers-error-translate.ts";

const STATUS_COLOR: Record<string, string> = {
  queued: "#888", processing: "#2563eb", done: "#16a34a", failed: "#dc2626",
};

function elapsed(startedAt?: string | null): string {
  if (!startedAt) return "";
  const s = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function AdminTeardownsPage() {
  const [urls, setUrls] = useState("");
  const [jobs, setJobs] = useState<TeardownJob[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    try { setJobs(await teardownsApi.list()); } catch { /* keep last */ }
  }, []);

  // Poll on a fixed cadence set up ONCE. (Bug fix: `jobs` in the deps caused an infinite loop —
  // refresh() sets jobs → effect re-ran → refresh() again → hammered the API. The refresh
  // callback reads the latest jobs itself for the active-cadence check.)
  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 5000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [refresh]);

  const submit = async () => {
    const list = urls.split("\n").map((u) => u.trim()).filter(Boolean);
    if (!list.length) return;
    setSubmitting(true);
    try { await teardownsApi.submit(list); setUrls(""); await refresh(); }
    finally { setSubmitting(false); }
  };


  const anyActive = jobs.some((j) => j.status === "queued" || j.status === "processing");

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      {/* (X11 drop 1) covers reads from the engine — its own sign-in */}
      <EngineSignInBanner />
      <h1>Teardown Queue</h1>
      <p style={{ color: "#666" }}>
        Paste winning-video URLs (one per line). Each is torn down into its structure (shots, timing, captions, delivery)
        for research. Jobs process one at a time — teardown takes ~2–5 min each.
      </p>
      <textarea
        value={urls} onChange={(e) => setUrls(e.target.value)}
        placeholder={"https://www.tiktok.com/@user/video/123\nhttps://www.tiktok.com/@user/video/456"}
        rows={5} style={{ width: "100%", fontFamily: "monospace", padding: 8 }}
      />
      <button onClick={submit} disabled={submitting} style={{ marginTop: 8, padding: "8px 16px" }}>
        {submitting ? "Queuing…" : "Queue for teardown"}
      </button>
      {anyActive && <span style={{ marginLeft: 12, color: "#2563eb" }}>● working — refreshes live</span>}

      <table style={{ width: "100%", marginTop: 24, borderCollapse: "collapse" }}>
        <thead><tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
          <th>Video</th><th>Status</th><th>Shots</th><th>Decision</th><th></th>
        </tr></thead>
        <tbody>
          {jobs.map((j) => {
            const err = j.status === "failed" ? translateError(j.error ?? "") : null;
            return (
              <tr key={j.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.title || j.url}</td>
                <td style={{ color: STATUS_COLOR[j.status], fontWeight: 600 }}>
                  {j.status === "processing"
                    ? <>processing{j.stage ? ` — ${j.stage}` : ""} <span style={{ color: "#999", fontWeight: 400 }}>({elapsed(j.startedAt)})</span></>
                    : j.status}
                </td>
                <td>{j.shotCount ?? "—"}</td>
                <td>{j.decision === "undecided" ? "—" : j.decision}</td>
                <td>
                  {err && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ color: "#dc2626", fontSize: 12 }}>{err.message}</span>
                      {/* (owner, 2026-10-05) no Retry button: it called a route that never existed. Submitting the URL again
                          re-queues it — a failed teardown is not a duplicate (only a finished one is skipped). */}
                      {err.retryable && <span style={{ fontSize: 12, color: "#6b7280" }}>— submit the URL again to retry</span>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {jobs.length === 0 && (
            <tr><td colSpan={5} style={{ color: "#999", padding: 24, textAlign: "center" }}>
              Paste TikTok URLs above to start.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
